"use client"

import Link from "next/link"
import { ArrowRight, CheckCircle2, Sparkles } from "lucide-react"
import { motion } from "motion/react"

import { useSmoothScroll } from "@/hooks/use-smooth-scroll"
import { Badge } from "@/components/ui/badge"
import { buttonVariants } from "@/components/ui/button"
import { ScrollReveal } from "@/components/scroll-reveal"

export function HeroSection() {
  const handleSmoothScroll = useSmoothScroll()

  return (
    <section className="relative flex min-h-svh items-center overflow-hidden border-b py-20 sm:py-24">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-[linear-gradient(to_bottom,transparent,transparent_68%,color-mix(in_oklab,var(--border)_72%,transparent))]" />
        <div className="absolute -top-24 left-1/2 h-72 w-72 -translate-x-[65%] rounded-full bg-[color-mix(in_oklab,var(--primary)_18%,transparent)] blur-3xl sm:h-96 sm:w-96" />
        <div className="absolute top-20 left-1/2 h-64 w-64 -translate-x-[5%] rounded-full bg-[color-mix(in_oklab,var(--accent)_24%,transparent)] blur-3xl sm:h-80 sm:w-80" />
        <div className="absolute inset-0 [background-image:linear-gradient(to_right,color-mix(in_oklab,var(--border)_88%,transparent)_1px,transparent_1px),linear-gradient(to_bottom,color-mix(in_oklab,var(--border)_88%,transparent)_1px,transparent_1px)] [mask-image:linear-gradient(to_bottom,white,rgba(255,255,255,0.2))] [background-size:32px_32px] opacity-[0.18]" />
      </div>
      <div className="relative mx-auto w-full max-w-7xl px-4 sm:px-6">
        <div className="mx-auto flex max-w-4xl flex-col items-center text-center">
          <ScrollReveal y={14}>
            <Badge
              variant="outline"
              className="border-primary/20 bg-primary/5 text-primary mb-7 rounded-full px-3.5 py-1.5 text-xs font-medium shadow-sm backdrop-blur"
            >
              <Sparkles className="h-3.5 w-3.5" />
              One snippet. Every editor.
            </Badge>
          </ScrollReveal>
          <ScrollReveal delay={0.06} y={18}>
            <h1 className="max-w-4xl text-4xl leading-[1.04] font-semibold tracking-[-0.045em] text-balance sm:text-6xl lg:text-7xl">
              Turn code into snippets.
              <span className="from-primary to-primary/60 block bg-gradient-to-r bg-clip-text text-transparent">
                Without the busywork.
              </span>
            </h1>
          </ScrollReveal>
          <ScrollReveal delay={0.12} y={18} className="max-w-2xl">
            <p className="text-muted-foreground mt-7 text-base leading-relaxed text-balance sm:text-lg">
              Write your code once, preview every change live, then export a
              ready-to-use snippet for your favorite editor.
            </p>
          </ScrollReveal>
          <ScrollReveal delay={0.18} y={18} className="w-full sm:w-auto">
            <div className="mt-9 flex w-full flex-col items-center gap-3 sm:w-auto sm:flex-row">
              <Link
                href="/generate"
                className={buttonVariants({
                  variant: "default",
                  size: "lg",
                  className:
                    "shadow-[0_0_24px_color-mix(in_oklab,var(--primary)_55%,transparent)] transition-[color,background-color,box-shadow,transform] hover:-translate-y-0.5 hover:shadow-[0_0_36px_color-mix(in_oklab,var(--primary)_75%,transparent)]",
                })}
              >
                Create your snippet
                <ArrowRight className="h-4 w-4" />
              </Link>
              <motion.button
                type="button"
                whileTap={{ scale: 0.98 }}
                onClick={() => handleSmoothScroll("features")}
                aria-label="Explore features"
                className={buttonVariants({
                  variant: "outline",
                  size: "lg",
                })}
              >
                See how it works
              </motion.button>
            </div>
          </ScrollReveal>
          <ScrollReveal delay={0.24} y={14}>
            <div className="text-muted-foreground mt-7 flex items-center gap-2 text-xs sm:text-sm">
              <CheckCircle2 className="text-primary h-4 w-4 shrink-0" />
              <p>
                VS Code, Sublime Text, Atom, Zed, and Jupyter—all supported.
              </p>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>
  )
}
